/**
 * The HTTP status an axios-shaped error carries, or undefined for a network /
 * non-HTTP failure.
 */
function errorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/**
 * True for a 4xx — the server understood the request and rejected it. Repeating
 * it verbatim cannot change the answer: the entity is gone (404), the caller
 * lacks access (403), or the request is malformed (400/422).
 */
export function isClientError(error: unknown): boolean {
  const status = errorStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (!navigator.onLine) return false;
  if (isClientError(error)) return false;
  return failureCount < 3;
}

/**
 * Whether an automatic (focus / reconnect) refetch is worth firing.
 *
 * `staleTime: 0` + `refetchOnWindowFocus` means every query re-runs each time the
 * tab regains focus. For a query whose last attempt was a 4xx that is a request
 * guaranteed to fail again, and it repeats for as long as the tab stays open —
 * a browser left on a page holding one dangling reference becomes a permanent
 * 404 generator. On 2026-08-03 exactly that pattern held production's
 * frontend-4xx CloudWatch alarm at ~50 per 5 minutes until the tabs were closed.
 *
 * Retry back-off does NOT cover this: `shouldRetryQuery` already refuses to retry
 * a 4xx, so each focus produces exactly one fresh request — which is precisely
 * why the traffic is periodic rather than bursty.
 *
 * A 5xx or network failure still refetches on focus: those are transient, and
 * refocusing the tab is the natural "try again" gesture. Recovery from a 4xx is
 * unaffected too — mount, invalidation and explicit refetch all still fire, so a
 * reference that starts resolving loads on the next visit to the page.
 */
export function shouldRefetchOnFocus(query: { state: { error: unknown } }): boolean {
  return !isClientError(query.state.error);
}
